import type { OpenString } from "../../lib/rhythm/rhythmPathPlan";
import { StringNumberBadge } from "./StringNumberBadge";

/** Grosor visual por número de cuerda (6 = más gruesa). */
const STRING_STROKE: Record<OpenString, number> = {
  6: 3.2,
  5: 2.7,
  4: 2.2,
  3: 1.7,
  2: 1.25,
  1: 0.9,
};

/** Orden visual: 6ª arriba → 1ª abajo (como mirando el mástil). */
const STRING_ORDER: OpenString[] = [6, 5, 4, 3, 2, 1];

export interface OpenStringsPanelProps {
  /** Cuerda a destacar / vibrar. */
  activeString?: OpenString | null;
  /** Varias cuerdas a la vez (ej. rasgueo de las 6). */
  activeStrings?: OpenString[] | null;
  /** 0–1, refuerza la vibración si hay señal de mic. */
  intensity?: number;
  className?: string;
}

/**
 * Recorte de diapasón sin trastes: cuerdas al aire + círculo numerado.
 */
export function OpenStringsPanel({
  activeString = null,
  activeStrings = null,
  intensity = 0,
  className = "",
}: OpenStringsPanelProps) {
  const highlighted = new Set<OpenString>(
    activeStrings?.length
      ? activeStrings
      : activeString != null
        ? [activeString]
        : []
  );
  const anyOn = highlighted.size > 0;
  const buzz = anyOn
    ? Math.max(0.4, Math.min(1, intensity * 2.4 + 0.4))
    : 0;

  return (
    <div
      className={`flex flex-col items-center gap-2 ${className}`}
      role="img"
      aria-label={
        highlighted.size > 1
          ? "Rasgueo: todas las cuerdas"
          : activeString
            ? `Cuerda activa: ${activeString}`
            : "Cuerdas al aire"
      }
    >
      <p className="text-[10px] uppercase tracking-[0.14em] text-white/35">
        Cuerdas
      </p>

      <div
        className="relative w-[min(100%,200px)] sm:w-[220px] rounded-2xl border overflow-hidden"
        style={{
          borderColor: "rgba(255,255,255,0.1)",
          background:
            "linear-gradient(180deg, rgba(48,42,38,0.95) 0%, rgba(28,24,22,0.98) 100%)",
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.06), 0 12px 32px rgba(0,0,0,0.35)",
        }}
      >
        <div
          className="absolute left-0 top-0 bottom-0 w-2.5 z-10"
          style={{
            background: "linear-gradient(90deg, #d6d0c4 0%, #a8a194 100%)",
            boxShadow: "2px 0 8px rgba(0,0,0,0.35)",
          }}
          aria-hidden
        />

        <div className="flex flex-col justify-between py-4 pl-5 pr-3 min-h-[180px] sm:min-h-[200px]">
          {STRING_ORDER.map((s) => {
            const on = highlighted.has(s);
            const stroke = STRING_STROKE[s];
            return (
              <div key={s} className="relative flex items-center gap-2 h-6">
                <StringNumberBadge openString={s} active={on} size="sm" />
                <div className="relative flex-1 h-full flex items-center">
                  {on && (
                    <span
                      className="pointer-events-none absolute inset-0 rounded-full"
                      style={{
                        background: `radial-gradient(ellipse at 40% 50%, rgba(153,246,228,${0.14 + buzz * 0.2}) 0%, transparent 72%)`,
                      }}
                    />
                  )}
                  <svg
                    className="relative z-[1] w-full h-5 overflow-visible"
                    viewBox="0 0 160 20"
                    preserveAspectRatio="none"
                    aria-hidden
                  >
                    <line
                      x1="0"
                      y1="10"
                      x2="160"
                      y2="10"
                      stroke={
                        on
                          ? "rgba(153,246,228,0.35)"
                          : "rgba(230,220,200,0.28)"
                      }
                      strokeWidth={stroke}
                      strokeLinecap="round"
                    />
                    <path
                      d="M0 10 Q20 4 40 10 T80 10 T120 10 T160 10"
                      fill="none"
                      stroke={on ? "#99f6e4" : "rgba(230,220,200,0.55)"}
                      strokeWidth={stroke}
                      strokeLinecap="round"
                      opacity={on ? 1 : 0.85}
                      style={
                        on
                          ? {
                              transformOrigin: "80px 10px",
                              animation: `openStringBuzz ${0.2 + (1 - buzz) * 0.12}s ease-in-out infinite`,
                              filter: `drop-shadow(0 0 ${5 + buzz * 12}px rgba(153,246,228,${0.4 + buzz * 0.35}))`,
                            }
                          : undefined
                      }
                    />
                  </svg>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes openStringBuzz {
          0%, 100% { transform: translateY(-2.2px) scaleY(1); }
          50% { transform: translateY(2.2px) scaleY(1.08); }
        }
      `}</style>
    </div>
  );
}
